import { Text } from '@shared/components';
import FeedScrollContainer from '@shared/components/feed-scroll-container/FeedScrollContainer';
import { useEmailThreadContext } from '@shared/contexts/pluginContexts/EmailThreadContext';
import { useFeedContext } from '@shared/plugin/contexts/FeedContext';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import clsx from 'clsx';
import { reverse } from 'lodash';
import { useMemo } from 'react';
import { FeedItem } from './components/feed-item/FeedItem';
import {
  FeedFilterOption,
  FeedItem as FeedItemModel,
  FeedItemType,
} from './types';
import { itemComparator } from './utils/combineItems';
import { groupItemsByDate } from './utils/groupItemsByDate';

const ROOT = makeRootClassName('Feed');
const el = makeElementClassNameFactory(ROOT);

const FeedGroupingHeader = ({ header }: { header: string }) => {
  return (
    <div className={el`feed-grouping-header`}>
      <div className={el`feed-grouping-header-divider`} />
      <Text type="custom">{header}</Text>
      <div className={el`feed-grouping-header-divider`} />
    </div>
  );
};

const FeedGrouping = ({
  header,
  items,
}: {
  header?: string;
  items: FeedItemModel[];
}) => {
  const sortedItems = reverse(items.sort(itemComparator));
  return (
    <>
      {header && <FeedGroupingHeader header={header} />}
      {sortedItems.map((item, idx) => {
        return (
          <FeedItem
            key={`feed-item-${idx}-${item.createdAt}-${item.type}`}
            item={item}
          />
        );
      })}
    </>
  );
};

export const FeedTimeline = ({ className }: { className?: string }) => {
  const {
    feed: { items },
    filter,
  } = useFeedContext();

  const filteredItems = useMemo(
    () => filterFeedItems(items, filter.selectedOption),
    [items, filter.selectedOption]
  );

  const groupedItems = groupItemsByDate(filteredItems);
  const groupedItemsCount = groupedItems.reduce((acc, { items }) => {
    return acc + items.length;
  }, 0);

  return (
    <FeedScrollContainer
      className={clsx(el`feed-items`, className)}
      length={groupedItemsCount}
    >
      {groupedItems.map(({ header, items }) => {
        return <FeedGrouping key={header} header={header} items={items} />;
      })}
    </FeedScrollContainer>
  );
};

export const FeedSuggestedActions = () => {
  const { isComposeMode } = useEmailThreadContext();
  const {
    feed: { suggestedActions },
  } = useFeedContext();

  return (
    <div className={el`suggested-actions`}>
      {isComposeMode ? (
        <div className="flex w-full items-center justify-center p-4">
          <Text type="body-sm" className="px-4 py-2 text-center text-gray-400">
            You are viewing a draft message. To build orders or create quotes,
            select a sent or received message.
          </Text>
        </div>
      ) : (
        <div>
          {suggestedActions.map((item, idx) => {
            return (
              <FeedItem
                key={`feed-item-${idx}-${item.createdAt}-${item.type}`}
                item={item}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export const Feed = () => {
  return (
    <div className={el`feed-root`}>
      <FeedTimeline />
      <FeedSuggestedActions />
    </div>
  );
};

export function filterFeedItems(
  items: FeedItemModel[],
  selectedOption: FeedFilterOption
): FeedItemModel[] {
  if (selectedOption === FeedFilterOption.QUOTES) {
    return items.filter(
      (item) =>
        item.type === FeedItemType.JOB_CARD && item.data.job.quotes.length > 0
    );
  }

  if (selectedOption === FeedFilterOption.ORDERS) {
    return items.filter(
      (item) =>
        item.type === FeedItemType.JOB_CARD && item.data.job.orders.length > 0
    );
  }

  return items;
}
